import * as UserManagementSelectors from "@app/user/store/selectors";

import { Store, select } from "@ngrx/store";
import { filter, skipWhile, tap } from "rxjs/operators";

import { Injectable } from "@angular/core";
import { AuthUser } from "@app/auth/model/auth-user";
import { AuthState } from "@app/auth/store";
import { AuthSelectors } from "@app/auth/store/selectors";
import { EntityState } from "@app/entity/store/entity.state";
import { UserManagementActions } from "@app/user/store";
import { UserManagementState } from "@app/user/store/user-management.state";
import { DbObject } from "../model/db-object.model";

@Injectable({
    providedIn: "root",
})
export class CommonModelService {
    dispatchUserRoles() {
        this.store
            .pipe(
                select(AuthSelectors.selectGetUser),
                tap(a => {
                    this.dispatchGetUserRoles(a);
                })
            )
            .subscribe()
            .unsubscribe();
    }

    dispatchGetUserRoles(authUser?: AuthUser) {
        this.store.dispatch(UserManagementActions.UserRoleActions.load({ authUser }));
    }

    selectAllUserRoles() {
        return this.store.pipe(
            select(UserManagementSelectors.getAllUserRoles),
            skipWhile(ur => !ur || !ur.length)
        );
    }

    selectUserRoles() {
        return this.store.pipe(
            select(UserManagementSelectors.getUserRoles),
            skipWhile(ur => !ur || !ur.length)
        );
    }

    dispatchAndSelectAllUserRoles() {
        this.dispatchUserRoles();
        return this.selectAllUserRoles();
    }

    dispatchAndSelectUserRoles() {
        this.dispatchUserRoles();
        return this.selectUserRoles();
    }

    dispatchEntityUsers(entityId: number) {
        this.store.dispatch(UserManagementActions.UserActions.loadAllEntityUsers({ entityId }));
    }

    dispatchEntityCustomers(entityId: number) {
        this.store.dispatch(UserManagementActions.UserActions.loadAllEntityCustomers({ entityId }));
    }

    dispatchAllUsers() {
        this.store.dispatch(UserManagementActions.UserActions.loadAllEntityUsers(null));
    }

    dispatchAllCustomers() {
        this.store.dispatch(UserManagementActions.UserActions.loadAllEntityCustomers(null));
    }

    dispatchEntityUser(userId: number) {
        this.store.dispatch(UserManagementActions.UserActions.load({ userId }));
    }

    dispatchActiveProjectUser(userId: number) {
        this.store.dispatch(UserManagementActions.UserActions.loadActiveProjectUser({ userId }));
    }

    selectAuthEntityIdAndDispatchEntityUsers() {
        return this.store.pipe(
            select(AuthSelectors.selectAuthUserEntityId),
            filter(authEntityId => !!authEntityId),
            tap(authEntityId => this.dispatchEntityUsers(authEntityId))
        );
    }

    selectAuthEntityIdAndDispatchEntityCustomers() {
        return this.store.pipe(
            select(AuthSelectors.selectAuthUserEntityId),
            filter(authEntityId => !!authEntityId),
            tap(authEntityId => this.dispatchEntityCustomers(authEntityId))
        );
    }

    selectAuthUser() {
        return this.store.pipe(select(AuthSelectors.selectGetUser));
    }

    trackById<T extends DbObject>(item: T): number | string {
        return item.id;
    }

    isUserListLoaded() {
        return this.store.pipe(select(UserManagementSelectors.isUserListLoaded()));
    }

    constructor(protected readonly store: Store<UserManagementState | AuthState | EntityState>) {}
}
